import { TaskDueDateState } from "@jugl-web/domain-resources/tasks";
import {
  TaskDueDateFormDrawer,
  TaskDueDateFormPopover,
} from "@jugl-web/domain-resources/tasks/components/DueDatePickerPopover";
import {
  TicketDetailItem,
  TicketDetailItemPlaceholder,
} from "@jugl-web/domain-resources/tickets/components/TicketDetailItem";
import { TicketPropertyPill } from "@jugl-web/domain-resources/tickets/components/TicketPropertyPill";
import { isTicketOverdue } from "@jugl-web/domain-resources/tickets/utils/isTicketOverdue";
import { TicketStatus } from "@jugl-web/rest-api/tickets";
import { cx } from "@jugl-web/utils";
import { useFormattedDate } from "@jugl-web/utils/hooks/useFormattedDate";
import { useIsSmallOrBelow } from "@jugl-web/utils/hooks/useMediaQuery";
import { FC, useState } from "react";
import { ReactComponent as CalendarDueIcon } from "./assets/calendar-due.svg";
import { ReactComponent as CalendarIcon } from "./assets/calendar.svg";

interface DueToDetailItemProps {
  dueDate: Date | null;
  status: TicketStatus;
  onChange: (dueDate: Date | null) => void;
}

export const DueToDetailItem: FC<DueToDetailItemProps> = ({
  dueDate,
  status,
  onChange,
}) => {
  const { formatDateTimeLabel } = useFormattedDate();

  const isSmallViewport = useIsSmallOrBelow();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const isOverdue = isTicketOverdue({ dueDate, status });

  const handleSubmit = (state: TaskDueDateState | null) => {
    const newDueDate = state ? state.date : null;

    if (JSON.stringify(newDueDate) === JSON.stringify(dueDate)) {
      return;
    }

    onChange(newDueDate);
  };

  const Icon = isOverdue ? CalendarDueIcon : CalendarIcon;

  const content = dueDate ? (
    <TicketPropertyPill
      icon={<Icon className="shrink-0" />}
      label={formatDateTimeLabel(dueDate, "concise")}
      className={cx(isOverdue && "border-tertiary-300")}
    />
  ) : (
    <TicketDetailItemPlaceholder />
  );

  if (isSmallViewport) {
    return (
      <>
        <TicketDetailItem
          isInteractive
          isActive={isDrawerOpen}
          label="Due to"
          content={content}
          onClick={() => setIsDrawerOpen(true)}
        />
        <TaskDueDateFormDrawer
          isOpen={isDrawerOpen}
          initialState={dueDate ? { date: dueDate } : null}
          onSubmit={handleSubmit}
          onClose={() => setIsDrawerOpen(false)}
        />
      </>
    );
  }

  return (
    <TaskDueDateFormPopover
      placement="bottom"
      className="w-[375px]"
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          as={TicketDetailItem}
          ref={triggerRef}
          isInteractive
          isActive={isOpen}
          label="Due to"
          content={content}
        />
      )}
      initialState={dueDate ? { date: dueDate } : null}
      onSubmit={handleSubmit}
    />
  );
};
